import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import {
  FALLBACK_CURRENCY,
  countryFromBrowserLocale,
  currencyForCountry,
  formatMoney,
  getCountryConfig,
  roundMoney,
} from "@/lib/currency";

const STORAGE_KEY = "taskratel-display-currency";
const APPROX_KEY = "taskratel-show-approx";

interface RateRow {
  base_currency: string;
  target_currency: string;
  rate: number;
  inverse_rate: number;
}

interface CurrencyContextValue {
  /** User's chosen reference/display currency. Never a settlement currency. */
  displayCurrency: string;
  setDisplayCurrency: (code: string) => void;
  /** Country the user is associated with (profile first, locale as hint). */
  countryId: string | null;
  showApprox: boolean;
  setShowApprox: (value: boolean) => void;
  ratesReady: boolean;
  /** Informational display conversion only — never used for settlement. */
  convert: (amount: number, from: string, to?: string) => number | null;
  /** "≈ C$18.40" or null when no rate/needless. */
  approxLabel: (amount: number | null | undefined, from: string) => string | null;
  currencyForCountry: (countryId?: string | null) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [displayCurrency, setDisplayCurrencyState] = useState<string>(() => {
    if (typeof window === "undefined") return "NGN";
    return (
      localStorage.getItem(STORAGE_KEY) ||
      currencyForCountry(countryFromBrowserLocale()?.country_id) ||
      FALLBACK_CURRENCY
    );
  });
  const [showApprox, setShowApproxState] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(APPROX_KEY) !== "false";
  });
  const [countryId, setCountryId] = useState<string | null>(null);
  const [rates, setRates] = useState<RateRow[]>([]);
  const [ratesReady, setRatesReady] = useState(false);

  // Display exchange rates (informational). Settlement rates come from the gateway.
  useEffect(() => {
    let cancelled = false;
    supabase
      .from("currency_rates")
      .select("base_currency, target_currency, rate, inverse_rate")
      .eq("is_active", true)
      .then(({ data }) => {
        if (cancelled) return;
        setRates((data as RateRow[]) || []);
        setRatesReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Profile preference wins over local storage for signed-in users.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase
      .from("profiles")
      .select("country_id, display_currency, show_approx_conversion")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled || !data) return;
        if (data.country_id) setCountryId(data.country_id);
        if (data.display_currency) {
          setDisplayCurrencyState(data.display_currency);
          localStorage.setItem(STORAGE_KEY, data.display_currency);
        } else if (data.country_id && !localStorage.getItem(STORAGE_KEY)) {
          setDisplayCurrencyState(currencyForCountry(data.country_id));
        }
        if (typeof data.show_approx_conversion === "boolean") {
          setShowApproxState(data.show_approx_conversion);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const setDisplayCurrency = useCallback(
    (code: string) => {
      setDisplayCurrencyState(code);
      localStorage.setItem(STORAGE_KEY, code);
      if (user) {
        supabase.from("profiles").update({ display_currency: code }).eq("user_id", user.id).then(() => {});
      }
    },
    [user],
  );

  const setShowApprox = useCallback(
    (value: boolean) => {
      setShowApproxState(value);
      localStorage.setItem(APPROX_KEY, String(value));
      if (user) {
        supabase.from("profiles").update({ show_approx_conversion: value }).eq("user_id", user.id).then(() => {});
      }
    },
    [user],
  );

  // Rates are stored NGN-based; pivot through NGN when needed.
  const rateFrom = useCallback(
    (from: string, to: string): number | null => {
      if (from === to) return 1;
      const direct = rates.find((r) => r.base_currency === from && r.target_currency === to);
      if (direct?.rate) return Number(direct.rate);
      const inverse = rates.find((r) => r.base_currency === to && r.target_currency === from);
      if (inverse?.rate) return 1 / Number(inverse.rate);
      const ngnToFrom = rates.find((r) => r.base_currency === "NGN" && r.target_currency === from);
      const ngnToTo = rates.find((r) => r.base_currency === "NGN" && r.target_currency === to);
      if (from === "NGN" && ngnToTo?.rate) return Number(ngnToTo.rate);
      if (to === "NGN" && ngnToFrom?.rate) return 1 / Number(ngnToFrom.rate);
      if (ngnToFrom?.rate && ngnToTo?.rate) return Number(ngnToTo.rate) / Number(ngnToFrom.rate);
      return null;
    },
    [rates],
  );

  const convert = useCallback(
    (amount: number, from: string, to?: string): number | null => {
      const target = (to || displayCurrency).toUpperCase();
      const rate = rateFrom(from.toUpperCase(), target);
      if (rate === null) return null;
      return roundMoney(amount * rate, target);
    },
    [displayCurrency, rateFrom],
  );

  const approxLabel = useCallback(
    (amount: number | null | undefined, from: string): string | null => {
      if (!showApprox || amount === null || amount === undefined) return null;
      const target = displayCurrency.toUpperCase();
      if (!from || from.toUpperCase() === target) return null;
      const converted = convert(amount, from, target);
      if (converted === null) return null;
      return formatMoney(converted, target, { approx: true });
    },
    [convert, displayCurrency, showApprox],
  );

  const value = useMemo<CurrencyContextValue>(
    () => ({
      displayCurrency,
      setDisplayCurrency,
      countryId,
      showApprox,
      setShowApprox,
      ratesReady,
      convert,
      approxLabel,
      currencyForCountry: (id?: string | null) =>
        currencyForCountry(id ?? countryId ?? getCountryConfig(countryId)?.country_id),
    }),
    [approxLabel, convert, countryId, displayCurrency, ratesReady, setDisplayCurrency, setShowApprox, showApprox],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within a CurrencyProvider");
  return ctx;
}